import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { Card } from '@/components/ui/card'
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemTitle,
} from '@/components/ui/item'
import { Button } from '@/components/ui/button'
import {
  useSearchCanonicalStandardsInfinite,
  type StandardPickerItem,
} from '@/shared/hooks/api/standards'
import { cn } from '@/shared/lib/utils'

const SEARCH_DEBOUNCE_MS = 300

export type CanonicalStandardPickerListProps = {
  enabled: boolean
  search: string
  types?: string[]
  excludeReferences: Set<string> | string[]
  selectedId: string | null
  onSelect: (item: StandardPickerItem) => void
  renderBadge?: (item: StandardPickerItem) => ReactNode
  renderTypeLabel?: (type: string) => ReactNode
  listVariant?: 'card' | 'item'
  disabled?: boolean
}

export function CanonicalStandardPickerList({
  enabled,
  search,
  types,
  excludeReferences,
  selectedId,
  onSelect,
  renderBadge,
  renderTypeLabel,
  listVariant = 'item',
  disabled = false,
}: CanonicalStandardPickerListProps) {
  const { t } = useTranslation('common')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch(search.trim())
    }, SEARCH_DEBOUNCE_MS)
    return () => {
      window.clearTimeout(handle)
    }
  }, [search])

  const excludeReferencesList = useMemo(() => {
    if (excludeReferences instanceof Set) {
      return Array.from(excludeReferences)
    }
    return excludeReferences
  }, [excludeReferences])

  const searchFilters = useMemo(
    () => ({
      query: debouncedSearch,
      types,
      excludeReferences: excludeReferencesList,
    }),
    [debouncedSearch, types, excludeReferencesList]
  )

  const {
    data,
    isPending,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
  } = useSearchCanonicalStandardsInfinite(searchFilters, {
    enabled: enabled && debouncedSearch.length >= 2,
  })

  const items = useMemo(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data]
  )

  const showSearchMinChars = debouncedSearch.length < 2
  const showNoResults =
    !showSearchMinChars && !isPending && items.length === 0 && error == null

  if (showSearchMinChars) {
    return (
      <p className='py-4 text-center text-sm text-muted-foreground'>
        {t('canonicalStandardPicker.searchMinChars')}
      </p>
    )
  }

  if (isPending && items.length === 0) {
    return (
      <div className='flex justify-center py-8'>
        <Loader2 className='size-8 animate-spin text-muted-foreground' />
      </div>
    )
  }

  if (error != null) {
    return (
      <p className='py-4 text-center text-sm text-destructive'>
        {error instanceof Error ? error.message : String(error)}
      </p>
    )
  }

  if (showNoResults) {
    return (
      <p className='py-4 text-center text-sm text-muted-foreground'>
        {t('canonicalStandardPicker.noResults')}
      </p>
    )
  }

  if (listVariant === 'card') {
    return (
      <div className='space-y-2'>
        <ul className='space-y-2'>
          {items.map((item) => (
            <li key={item.id}>
              <Card>
                <button
                  type='button'
                  onClick={() => onSelect(item)}
                  disabled={disabled}
                  className={cn(
                    'w-full rounded-lg p-3 text-left transition-colors',
                    selectedId === item.id
                      ? 'bg-primary/10 ring-2 ring-primary ring-inset'
                      : 'hover:bg-muted'
                  )}
                >
                  <span className='block text-sm font-medium'>
                    {item.reference}
                  </span>
                  <span className='block line-clamp-2 text-sm text-muted-foreground'>
                    {item.title}
                  </span>
                  {item.type ? (
                    <span className='block text-xs text-muted-foreground'>
                      {renderTypeLabel != null
                        ? renderTypeLabel(item.type)
                        : item.type}
                    </span>
                  ) : null}
                </button>
              </Card>
            </li>
          ))}
        </ul>
        {hasNextPage && (
          <div className='flex justify-center pt-2'>
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage || disabled}
            >
              {isFetchingNextPage && (
                <Loader2 className='me-2 size-4 animate-spin' />
              )}
              {t('canonicalStandardPicker.loadMore')}
            </Button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className='space-y-2'>
      <ItemGroup>
        {items.map((item) => (
          <Item
            key={item.id}
            variant='muted'
            className='hover:bg-muted/70'
            render={
              <button
                type='button'
                onClick={() => onSelect(item)}
                disabled={disabled}
                className={cn(
                  selectedId === item.id && 'ring-2 ring-primary ring-inset'
                )}
              />
            }
          >
            <ItemContent>
              <div className='flex flex-wrap items-center gap-2'>
                <ItemTitle>{item.reference}</ItemTitle>
                {renderBadge?.(item)}
              </div>
              <ItemDescription className='line-clamp-2'>
                {item.title}
              </ItemDescription>
              {item.type && renderTypeLabel != null ? (
                <ItemDescription className='text-xs'>
                  {renderTypeLabel(item.type)}
                </ItemDescription>
              ) : null}
            </ItemContent>
          </Item>
        ))}
      </ItemGroup>
      {hasNextPage && (
        <div className='flex justify-center pt-2'>
          <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage || disabled}
          >
            {isFetchingNextPage && (
              <Loader2 className='me-2 size-4 animate-spin' />
            )}
            {t('canonicalStandardPicker.loadMore')}
          </Button>
        </div>
      )}
    </div>
  )
}
